import React, { createContext, useContext, useState, useCallback } from 'react';
import { errorToast, successToast, getFileType } from '../utils/helper';
import axios from 'axios';
import { createRecipe, updateRecipe } from '../api/healthAPI';

// Create the context
const RecipeCreationContext = createContext();

// Custom hook to use the context
export const useRecipeCreation = () => {
  const context = useContext(RecipeCreationContext);
  if (!context) {
    throw new Error('useRecipeCreation must be used within a RecipeCreationProvider');
  }
  return context;
};

// Context provider component
export const RecipeCreationProvider = ({ children }) => {
  // State for managing the recipe creation process
  const [recipeData, setRecipeData] = useState({
    // Step 1: Recipe Details
    title: '',
    cooking_duration: 25,
    servings: 2,
    difficulty: '',
    category: '',
    image_url: '',
    thumbnailFile: null,

    // Step 2: Preparation Steps
    preparation_steps: [],

    // Step 3: Nutritional Value
    nutritional_value: [],

    // Step 4: Add-ons
    ingredients: [],
    chefs_note: '',
    cover_image: null,
    cover_image_preview: null,

    // Meta information
    recipeId: null,
    currentStep: 1,
    isLoading: false,
    errors: {}
  });

  // Update recipe data
  const updateRecipeData = useCallback((updates) => {
    setRecipeData((prev) => ({
      ...prev,
      ...updates,
      errors: {} // Clear errors when updating data
    }));
  }, []);
  console.log('RecipeCreationContext recipeData:', recipeData);
  // Set errors
  const setErrors = useCallback((errors) => {
    setRecipeData((prev) => ({
      ...prev,
      errors
    }));
  }, []);

  // Validate step 1 data
  const validateStep1 = useCallback((data) => {
    const errors = {};

    if (!data.title?.trim()) {
      errors.title = 'Recipe title is required';
    }

    if (!data.difficulty) {
      errors.difficulty = 'Difficulty level is required';
    }

    if (!data.category) {
      errors.category = 'Category is required';
    }

    if (!data.cooking_duration || data.cooking_duration <= 0) {
      errors.cooking_duration = 'Valid cooking duration is required';
    }

    if (!data.servings || data.servings <= 0) {
      errors.servings = 'Valid number of servings is required';
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  // Validate step 2 - Preparation Steps
  const validateStep2 = useCallback((data) => {
    const errors = {};

    if (!data.preparation_steps || data.preparation_steps.length === 0) {
      errors.preparation_steps = 'At least one preparation step is required';
    } else {
      const emptySteps = data.preparation_steps.filter((step) => !step.description?.trim());
      if (emptySteps.length > 0) {
        errors.preparation_steps = 'All preparation steps must have a description';
      }
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  // Validate step 3 - Nutritional Value
  const validateStep3 = useCallback((data) => {
    const errors = {};

    if (!data.nutritional_value || data.nutritional_value.length === 0) {
      errors.nutritional_value = 'Nutritional information is required';
    } else {
      const hasValues = data.nutritional_value.some(
        (item) => item.amount && parseFloat(item.amount) > 0
      );
      if (!hasValues) {
        errors.nutritional_value = 'At least one nutritional value must be greater than 0';
      }
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, []);

  // Validate current step
  const validateCurrentStep = useCallback(
    (step) => {
      switch (step) {
        case 1:
          return validateStep1(recipeData);
        case 2:
          return validateStep2(recipeData);
        case 3:
          return validateStep3(recipeData);
        case 4:
          // Step 4 is optional (additional ingredients)
          return { errors: {}, isValid: true };
        default:
          return { errors: {}, isValid: true };
      }
    },
    [recipeData, validateStep1, validateStep2, validateStep3]
  );

  // Save current step as draft
  const saveCurrentStep = useCallback(
    async (step, statusValue = 'draft') => {
      try {
        const isEditing = !!recipeData.recipeId;
        console.log(`Saving step ${step} as ${statusValue}:`, recipeData);
        setRecipeData((prev) => ({ ...prev, isLoading: true }));

        // Validate the current step
        const { errors, isValid } = validateCurrentStep(step);
        if (!isValid) {
          setErrors(errors);
          const firstError = Object.values(errors)[0];
          errorToast(firstError);
          setRecipeData((prev) => ({ ...prev, isLoading: false }));
          return { success: false, errors };
        }

        // Build payload based on current step
        const payload = {
          current_step: step,
          status: statusValue,
          is_active: statusValue === 'published'
        };

        // Step 1: Recipe Details
        if (step >= 1) {
          payload.title = recipeData.title;
          payload.difficulty = recipeData.difficulty;
          payload.cooking_duration = recipeData.cooking_duration;
          payload.servings = recipeData.servings;
          payload.category = recipeData.category;
          payload.ingredients = recipeData.ingredients.map(({ id, _id, ...rest }) => rest);
        }

        // Step 2: Preparation Steps
        if (step >= 2) {
          payload.preparation_steps = recipeData.preparation_steps.map(
            ({ id, _id, ...rest }) => rest
          );
        }

        // Step 3: Nutritional Value
        if (step >= 3) {
          payload.nutritional_value = recipeData.nutritional_value.map(
            ({ id, _id, ...rest }) => rest
          );
        }

        // Step 4: Add-ons
        if (step >= 4) {
          payload.notes = recipeData.chefs_note || '';
          // Add image_url only if cover_image exists (new upload)
          if (recipeData.cover_image) {
            payload.image_url = getFileType(recipeData.cover_image);
          }
        }

        console.log(
          isEditing ? 'Updating' : 'Creating',
          `recipe at step ${step} with payload:`,
          payload
        );

        // Call appropriate API based on whether editing or creating
        const response = isEditing
          ? await updateRecipe(recipeData.recipeId, payload)
          : await createRecipe(payload);

        if (response?.meta?.code === 1 && response?.data?._id) {
          // Upload image if presigned URL is provided and there's a new cover_image
          if (response?.data?.imagePreSignUrl?.uploadURL && recipeData.cover_image) {
            await axios.put(response.data.imagePreSignUrl.uploadURL, recipeData.cover_image, {
              headers: {
                'content-type': `${recipeData.cover_image.type.split('/')[0]}/${
                  recipeData.cover_image.name.split('.')[1]
                }`
              }
            });
          }

          // Update context with recipe ID if it's a new recipe
          if (!isEditing) {
            setRecipeData((prev) => ({ ...prev, recipeId: response.data._id }));
          }

          if (statusValue === 'published') {
            successToast('Recipe published successfully!');
          }

          return {
            success: true,
            recipeId: response.data._id,
            data: response.data
          };
        } else {
          throw new Error(response?.meta?.message || `Failed to save recipe`);
        }
      } catch (error) {
        console.error('Error saving recipe:', error);
        errorToast(error.message || 'Failed to save recipe');

        return {
          success: false,
          error: error.message
        };
      } finally {
        setRecipeData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [recipeData, validateCurrentStep, setErrors]
  );

  // Submit recipe (create or update) - kept for backward compatibility
  const submitRecipe = useCallback(async () => {
    return saveCurrentStep(4, 'published');
  }, [saveCurrentStep]);

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    setRecipeData((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4)
    }));
  }, []);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    setRecipeData((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1)
    }));
  }, []);

  // Context value
  const value = {
    recipeData,
    recipeId: recipeData.recipeId,
    currentStep: recipeData.currentStep,
    isLoading: recipeData.isLoading,
    errors: recipeData.errors,
    updateRecipeData,
    setErrors,
    submitRecipe,
    saveCurrentStep,
    goToNextStep,
    goToPreviousStep,
    validateStep1,
    validateStep2,
    validateStep3,
    validateCurrentStep
  };

  return <RecipeCreationContext.Provider value={value}>{children}</RecipeCreationContext.Provider>;
};
